import { css } from '@emotion/react';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import { typeStyle } from 'constants/typography';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'types/theme.types';
import { BridgedModal as Modal } from 'components/common/modal';
import { FormattedMessage, useIntl } from 'react-intl';
import { useInputState } from 'hooks/utils.hooks';
import Button from 'components/common/button';
import type { ProjectFormErrors } from 'components/projects/create-project-form';
import CreateProjectForm from 'components/projects/create-project-form';
import useStores from 'hooks/use-stores.hooks';
import { observer } from 'mobx-react';
import { useCreateProject, useGetProjects } from 'stores/rq/projects';

export interface CreateProjectModalProps {
  parentProjectId?: string;
  hideModal: () => void;
  open: boolean;
}

const CreateProjectModal: React.FunctionComponent<CreateProjectModalProps> = ({ parentProjectId, open, hideModal }) => {
  const intl = useIntl();
  const [name, setName] = useInputState();
  const [description, setDescription] = useInputState();
  const [errors, setErrors] = useState<ProjectFormErrors>({});
  const [isLoading, setLoading] = useState(false);
  const {
    organizationsStore: { currentOrganizationId }
  } = useStores();
  const { mutateAsync: createProject } = useCreateProject(parentProjectId);
  // Only while the modal is open: the modal is mounted by pages that deliberately never load the full
  // project list, and the sibling-name warning is the only thing that needs it.
  const { data: projects } = useGetProjects({ enabled: open });
  const navigate = useNavigate();

  const titleId = !!parentProjectId ? 'projects.add.new.sub-project' : 'projects.add.new';

  const nameWarning = useMemo(() => {
    const candidate = name?.trim().toLowerCase();
    if (!candidate) return null;

    const hasSameNameSibling = (projects ?? []).some(
      project =>
        (project.parentProjectId ?? null) === (parentProjectId ?? null) &&
        project.name.trim().toLowerCase() === candidate
    );

    return hasSameNameSibling ? intl.formatMessage({ id: 'projects.create.modal.duplicate.sibling.name' }) : null;
  }, [projects, parentProjectId, name, intl]);

  const mergeErrors = useCallback(
    (newErrors: Partial<ProjectFormErrors>) =>
      setErrors({
        ...errors,
        ...newErrors
      }),
    [errors, setErrors]
  );

  const onChangeName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      mergeErrors({
        common: null,
        name: null
      });

      setName(event);
    },
    [mergeErrors, setName]
  );

  const onChangeDescription = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      mergeErrors({ common: null });
      setDescription(event);
    },
    [setDescription, mergeErrors]
  );

  const onCreateClick = async () => {
    if (!name) {
      mergeErrors({
        name: intl.formatMessage({ id: 'projects.create.modal.empty.name' })
      });
      return;
    }

    setLoading(true);
    try {
      const createdProject = await createProject({
        name,
        description,
        organizationId: currentOrganizationId as string
      });
      setName('');
      setDescription('');
      navigate(`/p/${createdProject.id}/environments`);
    } catch (error) {
      mergeErrors({
        common: (error as Error).message
      });
    } finally {
      setLoading(false);
      hideModal();
    }
  };

  const title = (
    <Header>
      <Title>
        <FormattedMessage id={titleId} tagName="h1" />
      </Title>
    </Header>
  );

  const footer = (
    <Footer>
      <FooterButton onClick={hideModal} disabled={isLoading} data-e2e="create-project-modal-cancel">
        <FormattedMessage id="cancel" />
      </FooterButton>
      <FooterButton
        id="projects-create-submit"
        type="primary"
        onClick={onCreateClick}
        disabled={isLoading}
        isLoading={isLoading}
        data-e2e="create-project-modal-button">
        <FormattedMessage id="projects.button.create" />
      </FooterButton>
    </Footer>
  );

  return (
    <Modal
      size="large"
      open={open}
      onCancel={hideModal}
      width="525px"
      title={title}
      footer={footer}
      destroyOnClose
      centered>
      <CreateProjectForm
        name={name}
        description={description}
        onChangeName={onChangeName}
        onChangeDescription={onChangeDescription}
        errors={errors}
        nameWarning={nameWarning}
      />
    </Modal>
  );
};

// The bridge supplies the header inset on the new design (APO-462).
const Header = styled.div`
  ${legacyOnly(
    () => css`
      padding: 16px 8px 8px 16px;
    `
  )}
`;

/**
 * This modal composes its own `title` node, so `modal.tsx`'s heading never reaches it - the ramp has to be
 * applied here too (APO-462). Found in the browser: the heading rendered at 600 where the ramp gives 700.
 */
const Title = styled.div`
  ${legacyOnly(
    theme => css`
      h1 {
        color: ${theme.primaryBlack};
        font-size: 24px;
        font-weight: 600;
      }
    `
  )}

  ${newDesignOnly(
    () => css`
      h1 {
        ${typeStyle.headingH3}
        color: var(--text-heading);
        margin: 0;
      }
    `
  )}
`;

const Footer = styled.div`
  ${legacyOnly(css`
    padding: 10px 9px;
  `)}

  ${newDesignOnly(css`
    padding: var(--padding-xs);
  `)}
`;

const FooterButton = styled(Button)`
  ${legacyOnly(
    () => css`
      width: 140px !important;
    `
  )}
`;

export default observer(CreateProjectModal);
